import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { QRCodeSVG } from "qrcode.react";
import {
  CalendarDays, Clock, MapPin, UserCheck, Search, CheckCircle2,
  AlertCircle, ChevronDown, ChevronUp, Loader2, LogIn, Users,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(d) {
  if (!d) return "";
  try {
    return new Date(`${d}T00:00:00`).toLocaleDateString(undefined, {
      weekday: "long", month: "long", day: "numeric", year: "numeric",
    });
  } catch { return d; }
}
function formatTime(t) {
  if (!t) return "";
  try {
    const [h, m] = t.split(":");
    const d = new Date(); d.setHours(+h, +m, 0, 0);
    return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  } catch { return t; }
}

/**
 * Public event check-in. Uses the same color scheme as /login (learner
 * portal): soft gray gradient background, white card, `--primary-color`
 * accents. Distinct layout from /login so the two surfaces remain
 * functionally separate.
 */
export default function CheckInPage() {
  const { eventId } = useParams();
  const [event, setEvent] = useState(null);
  const [loadingEvent, setLoadingEvent] = useState(true);
  const [search, setSearch] = useState("");
  const [matches, setMatches] = useState([]);
  const [searching, setSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [checkedIn, setCheckedIn] = useState(null);
  const [walkinMode, setWalkinMode] = useState(false); // eslint-disable-line no-unused-vars
  // Walk-in state kept only so the "already checked in as a walk-in"
  // confirmation branch continues to render correctly for previously
  // recorded walk-in check-ins; the walk-in FORM is intentionally no
  // longer reachable from this page (attendees who can't find their
  // name must alert an administrator).
  const [walkin, setWalkin] = useState({ first_name: "", last_name: "", email: "" }); // eslint-disable-line no-unused-vars
  const [submitting, setSubmitting] = useState(false);
  const [checkinsList, setCheckinsList] = useState([]);
  const [showCheckins, setShowCheckins] = useState(false);
  const [loginAvailable, setLoginAvailable] = useState(false);

  // --- Load event ---------------------------------------------------------
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/public/events/${eventId}`);
        setEvent(data);
      } catch { setEvent({ error: true }); }
      finally { setLoadingEvent(false); }
    })();
  }, [eventId]);

  // --- Roster search ------------------------------------------------------
  const doSearch = useCallback(async (q) => {
    const query = (q ?? "").trim();
    if (query.length < 2) { setMatches([]); setHasSearched(false); return; }
    setSearching(true);
    try {
      const { data } = await axios.get(`${API}/public/events/${eventId}/roster`, { params: { q: query } });
      setMatches(data); setHasSearched(true);
    } finally { setSearching(false); }
  }, [eventId]);

  useEffect(() => {
    const t = setTimeout(() => doSearch(search), 250);
    return () => clearTimeout(t);
  }, [search, doSearch]);

  const refreshCheckins = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/events/${eventId}/checkins`);
      setCheckinsList(data);
    } catch { /* ignore */ }
  }, [eventId]);
  useEffect(() => { refreshCheckins(); }, [refreshCheckins]);

  // Live polling on the public kiosk page: every 5 seconds, silently
  // re-fetch the check-in roster so the "Who's checked in" list updates
  // in near-real-time as others scan the QR on their phones. Stops the
  // moment the tab is hidden to save mobile battery + data.
  useEffect(() => {
    if (!eventId) return undefined;
    const tick = () => {
      if (document.visibilityState === "visible") {
        refreshCheckins();
      }
    };
    const handle = setInterval(tick, 5000);
    document.addEventListener("visibilitychange", tick);
    return () => {
      clearInterval(handle);
      document.removeEventListener("visibilitychange", tick);
    };
  }, [eventId, refreshCheckins]);

  const refreshLoginAvailable = useCallback(async () => {
    if (!event?.cohort_id) return;
    try {
      const { data } = await axios.get(`${API}/cohorts/${event.cohort_id}/login-available`);
      setLoginAvailable(!!data.available);
    } catch { setLoginAvailable(false); }
  }, [event]);

  const checkInLearner = async (learner) => {
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/events/${eventId}/checkin`, { learner_id: learner.id });
      setCheckedIn({
        first_name: learner.first_name, last_name: learner.last_name,
        walkin: false, duplicate: !!data.duplicate,
      });
      await refreshCheckins(); await refreshLoginAvailable();
    } catch { setCheckedIn({ error: true }); }
    finally { setSubmitting(false); }
  };

  // Walk-in submission is no longer reachable from this page's UI. The
  // handler is retained (unexported, kept in scope) so a future admin
  // action or migration can reuse it, but the button that invoked it
  // has been removed per product decision — attendees who can't find
  // their name must alert an administrator instead of self-adding.
  // eslint-disable-next-line no-unused-vars
  const submitWalkin = async (e) => {
    e.preventDefault();
    if (!walkin.first_name.trim() || !walkin.last_name.trim()) return;
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/events/${eventId}/walkin`, walkin);
      setCheckedIn({
        first_name: data.learner.first_name, last_name: data.learner.last_name,
        walkin: true, duplicate: false,
      });
      await refreshCheckins(); await refreshLoginAvailable();
    } catch { setCheckedIn({ error: true }); }
    finally { setSubmitting(false); }
  };

  const resetCheckIn = () => {
    setCheckedIn(null); setSearch(""); setMatches([]); setHasSearched(false);
    setWalkinMode(false); setWalkin({ first_name: "", last_name: "", email: "" });
  };

  // --- Render -------------------------------------------------------------
  if (loadingEvent) {
    return (
      <Shell>
        <Card>
          <div className="flex justify-center py-20">
            <Loader2 className="h-6 w-6 animate-spin" style={{ color: "var(--primary-color, #4A90D9)" }} />
          </div>
        </Card>
      </Shell>
    );
  }
  if (!event || event.error) {
    return (
      <Shell>
        <Card>
          <div className="text-center space-y-3 py-6">
            <AlertCircle className="h-10 w-10 mx-auto text-red-500" />
            <h2 className="font-heading text-xl font-bold text-gray-900">Event not found</h2>
            <p className="text-sm text-gray-500 font-body">This check-in link is invalid or has been removed.</p>
          </div>
        </Card>
      </Shell>
    );
  }

  return (
    <Shell>
      {/* Event details card — QR lives in here next to the title */}
      <Card testid="checkin-event-card">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-[10px] font-body font-semibold uppercase tracking-[0.15em] px-2 py-1 rounded"
                style={{ backgroundColor: "color-mix(in srgb, var(--primary-color, #4A90D9) 12%, white)", color: "var(--primary-color, #4A90D9)" }}>
            Check-in
          </span>
        </div>
        <div className="flex items-start gap-4">
          <div className="flex-1 min-w-0">
            <h1 className="font-heading text-xl sm:text-2xl font-bold leading-tight text-gray-900" data-testid="checkin-event-name">
              {event.name}
            </h1>
            <div className="space-y-1.5 text-sm text-gray-600 mt-3">
              <Detail icon={CalendarDays} value={formatDate(event.date)} />
              <Detail icon={Clock} value={`${formatTime(event.start_time)}${event.end_time ? ` – ${formatTime(event.end_time)}` : ""}`} />
              {event.location && <Detail icon={MapPin} value={event.location} />}
              {event.instructor && <Detail icon={UserCheck} value={event.instructor} />}
            </div>
          </div>
          <div
            className="shrink-0 p-2 bg-white rounded-lg border border-gray-200"
            title="Scan to open on your phone"
            data-testid="checkin-qr-inline"
          >
            <QRCodeSVG
              value={window.location.href}
              size={96}
              level="M"
              includeMargin={false}
              bgColor="#FFFFFF"
              fgColor="#0A0C10"
            />
            <p className="text-[9px] font-body text-gray-500 text-center mt-1 uppercase tracking-wider">
              Scan to check in
            </p>
          </div>
        </div>
      </Card>

      {/* State machine — confirmation | search | walkin */}
      {checkedIn ? (
        <Card testid="checkin-confirm">
          <div className="text-center space-y-3 py-4">
            <div className="inline-flex items-center justify-center h-14 w-14 rounded-full"
                 style={{ backgroundColor: "color-mix(in srgb, var(--primary-color, #4A90D9) 15%, white)" }}>
              <CheckCircle2 className="h-8 w-8" style={{ color: "var(--primary-color, #4A90D9)" }} />
            </div>
            <h2 className="font-heading text-2xl font-bold text-gray-900">
              {checkedIn.duplicate ? "You're already checked in" : "You're checked in"}
            </h2>
            <p className="text-base font-body text-gray-600">
              {checkedIn.first_name} {checkedIn.last_name}
            </p>
            {checkedIn.walkin && (
              <p className="text-xs text-gray-500 font-body">
                Please let one of the instructors know you joined as a walk-in.
              </p>
            )}
            {loginAvailable && (
              <a
                href="/login"
                data-testid="checkin-login-btn"
                className="inline-flex items-center gap-2 mt-3 px-6 py-3 rounded-xl font-body font-medium text-sm transition-transform hover:scale-[1.02]"
                style={{ backgroundColor: "var(--primary-color, #4A90D9)", color: "var(--on-secondary, #FFFFFF)", minHeight: 44 }}
              >
                <LogIn className="h-4 w-4" /> Log in to access your training
              </a>
            )}
            <button
              onClick={resetCheckIn}
              data-testid="checkin-reset-btn"
              className="block mx-auto mt-2 text-xs font-body underline text-gray-500 hover:text-gray-700"
            >
              Check in someone else
            </button>
          </div>
        </Card>
      ) : (
        <Card testid="checkin-search-card">
          <h2 className="font-heading text-lg font-bold text-gray-900">Welcome</h2>
          <p className="text-sm font-body text-gray-600 mt-1">
            Please check in by entering your first or last name.
          </p>
          <div className="relative mt-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              inputMode="text"
              autoCapitalize="words"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Type your name…"
              data-testid="checkin-search-input"
              className="w-full pl-10 pr-3 rounded-xl font-body text-base bg-gray-50 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:bg-white transition-colors"
              style={{
                border: "1px solid #e5e7eb",
                minHeight: 52,
                "--tw-ring-color": "var(--primary-color, #4A90D9)",
              }}
            />
          </div>
          {searching && (
            <div className="flex items-center gap-2 text-xs text-gray-500 mt-3">
              <Loader2 className="h-3 w-3 animate-spin" /> Searching…
            </div>
          )}
          {matches.length > 0 && (
            <div className="space-y-2 mt-4" data-testid="checkin-matches">
              {matches.map((m) => (
                <button
                  key={m.id}
                  onClick={() => checkInLearner(m)}
                  disabled={submitting}
                  data-testid={`checkin-match-${m.id}`}
                  className="w-full text-left px-4 rounded-xl font-body text-base transition-all disabled:opacity-60 hover:-translate-y-[1px]"
                  style={{
                    backgroundColor: "color-mix(in srgb, var(--primary-color, #4A90D9) 8%, white)",
                    border: "1px solid color-mix(in srgb, var(--primary-color, #4A90D9) 25%, white)",
                    color: "var(--primary-color, #4A90D9)",
                    minHeight: 52,
                    fontWeight: 500,
                  }}
                >
                  {m.first_name} {m.last_name}
                </button>
              ))}
            </div>
          )}
          {hasSearched && !searching && matches.length === 0 && (
            // Walk-in self-add has been intentionally removed. Attendees
            // who can't find themselves on the roster must alert an
            // administrator instead of adding themselves.
            <div
              className="rounded-xl p-4 mt-4 space-y-2 bg-amber-50 border border-amber-200"
              data-testid="checkin-no-match"
            >
              <p className="text-sm font-body font-semibold text-amber-900">
                We couldn't find your name on the roster.
              </p>
              <p className="text-xs font-body text-amber-800">
                Please alert an administrator or one of the instructors — they will add you and check you in.
              </p>
            </div>
          )}
        </Card>
      )}

      {/* Collapsible — who's checked in */}
      <Card padded={false} testid="checkin-list-card">
        <button
          onClick={() => setShowCheckins(!showCheckins)}
          className="w-full flex items-center justify-between px-5 py-4 text-gray-900"
          data-testid="checkin-list-toggle"
        >
          <span className="flex items-center gap-2 font-body font-semibold text-sm">
            <Users className="h-4 w-4" /> Who's checked in
            <span className="text-xs font-normal px-2 py-0.5 rounded-full"
                  style={{ backgroundColor: "color-mix(in srgb, var(--primary-color, #4A90D9) 12%, white)", color: "var(--primary-color, #4A90D9)" }}>
              {checkinsList.length}
            </span>
          </span>
          {showCheckins ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
        </button>
        {showCheckins && (
          <div className="border-t border-gray-100 px-5 py-3">
            {checkinsList.length === 0 ? (
              <p className="text-xs py-2 text-gray-400">No one's checked in yet.</p>
            ) : (
              <ul className="space-y-2">
                {checkinsList.map((c) => (
                  <li key={`${c.learner_id}-${c.timestamp}`} className="flex items-center justify-between gap-3 text-sm" data-testid={`checkin-list-${c.learner_id}`}>
                    <span className="font-body text-gray-900">
                      {c.first_name} {c.last_name}
                      {c.walkin && (
                        <span className="ml-2 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">
                          walk-in
                        </span>
                      )}
                    </span>
                    <span className="text-xs font-mono text-gray-400">
                      {new Date(c.timestamp).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </Card>
    </Shell>
  );
}

// -------- helpers (match /login color scheme) --------

function Shell({ children }) {
  return (
    <div
      className="min-h-screen py-6 px-4 sm:px-6 flex items-start justify-center relative overflow-hidden bg-gradient-to-br from-[#F8F9FC] via-white to-[#EEF1F8]"
      data-testid="checkin-page"
    >
      <div className="absolute top-[-120px] right-[-80px] w-[400px] h-[400px] rounded-full opacity-20 pointer-events-none"
           style={{ background: "radial-gradient(circle, var(--primary-color, #4A90D9) 0%, transparent 70%)" }} />
      <div className="absolute bottom-[-100px] left-[-60px] w-[300px] h-[300px] rounded-full opacity-15 pointer-events-none"
           style={{ background: "radial-gradient(circle, var(--primary-color, #4A90D9) 0%, transparent 70%)" }} />
      <div className="relative z-10 w-full max-w-md space-y-4">
        <div className="text-center pt-2 pb-1">
          <h1 className="font-heading text-2xl font-bold text-gray-900 tracking-tight">
            DiNA<span style={{ color: "var(--primary-color, #4A90D9)" }}>XLP</span>
          </h1>
        </div>
        {children}
      </div>
    </div>
  );
}

function Card({ children, testid, padded = true }) {
  return (
    <div
      className={`bg-white border border-gray-200 rounded-2xl shadow-xl shadow-gray-200/50 ${padded ? "p-5 sm:p-6" : ""}`}
      data-testid={testid}
    >
      {children}
    </div>
  );
}

function Detail({ icon: Icon, value }) {
  return (
    <div className="flex items-start gap-2">
      <Icon className="h-4 w-4 shrink-0 mt-0.5" style={{ color: "var(--primary-color, #4A90D9)" }} />
      <span className="flex-1">{value}</span>
    </div>
  );
}

function Field({ label, value, onChange, required, type = "text", testid }) {
  return (
    <div className="space-y-1.5">
      <label className="block text-xs font-body font-semibold uppercase tracking-wider text-gray-500">
        {label}{required && <span className="text-red-500"> *</span>}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        data-testid={testid}
        autoCapitalize={type === "email" ? "none" : "words"}
        className="w-full px-3 rounded-xl font-body text-base bg-gray-50 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:bg-white transition-colors"
        style={{
          border: "1px solid #e5e7eb",
          minHeight: 44,
          "--tw-ring-color": "var(--primary-color, #4A90D9)",
        }}
      />
    </div>
  );
}
